import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Stack,
  CircularProgress
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SearchIcon from '@mui/icons-material/Search';
import Images from '../../utils/Images';
import { CategoryFilterChip } from '../../components/FilterChip';
import axios from 'axios';
import { API } from '../../utils/Api';

function Products() {
  const token = localStorage.getItem('token');
  const outletId = localStorage.getItem('outletId');
  const [selectedChipIndex, setSelectedChipIndex] = useState(null);

  // Full category list from getProductsWithAddons
  const [categories, setCategories] = useState([]);

  // Master product list (flattened from categories)
  const [allProducts, setAllProducts] = useState([]);

  // Currently visible products (after filters)
  const [products, setProducts] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);

  const [recipesMap, setRecipesMap] = useState({});
  const [outletData] = useState(JSON.parse(localStorage.getItem('outletData')));

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  /**
   * Build a flat product list from categories array
   * Each product gets a `category` object attached so UI can still use
   * `product.category.name`
   */
  const flattenCategoriesToProducts = (categoryList) => {
    if (!Array.isArray(categoryList)) return [];

    return categoryList.flatMap((cat) =>
      (cat.products || []).map((prod) => ({
        ...prod,
        category: {
          _id: cat._id,
          name: cat.name,
          description: cat.description
        }
      }))
    );
  };

  /**
   * Client-side filter based on search text and categoryId
   * (Previously this was making an API call to /product/List)
   */
  const filterProducts = (searchText = '', categoryId = null) => {
    const search = (searchText || '').toLowerCase();

    let filtered = allProducts;

    if (categoryId) {
      filtered = filtered.filter(
        (p) => p.category && p.category._id === categoryId
      );
    }

    if (search) {
      filtered = filtered.filter((p) => {
        const nameMatch = p.name?.toLowerCase().includes(search);
        const descMatch = p.description
          ?.toLowerCase()
          .includes(search);
        const keywordsMatch = p.keywords
          ?.toLowerCase()
          .includes(search);
        return nameMatch || descMatch || keywordsMatch;
      });
    }

    setProducts(filtered);
  };

  const handleChipClick = (categoryId, index) => {
    if (index === selectedChipIndex) {
      // Unselect chip → show all products (respecting search)
      setSelectedChipIndex(null);
      filterProducts(searchQuery, null);
    } else {
      setSelectedChipIndex(index);
      filterProducts(searchQuery, categoryId);
    }
  };

  const handleSearch = (searchText) => {
    setSearchQuery(searchText);

    let categoryId = null;
    if (selectedChipIndex !== null && categories[selectedChipIndex]) {
      categoryId = categories[selectedChipIndex]._id;
    }

    filterProducts(searchText, categoryId);
  };

  /**
   * NEW: Single API to fetch categories + products with addons
   * /product/getProductsWithAddons?outletId=...
   */
  const getCategoriesProductsList = async () => {
    if (!outletData?._id) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `${API.BASE_URL}product/getProductsWithAddons`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: {
            outletId: outletData._id
          }
        }
      );

      const fetchedCategories = response?.data?.data?.categories || [];
      setCategories(fetchedCategories);

      const flattened = flattenCategoriesToProducts(fetchedCategories);
      setAllProducts(flattened);
      setProducts(flattened);

      // If nothing selected yet, auto-select first product (optional)
      // if (!selectedProduct && flattened.length > 0) {
      //   setSelectedProduct(flattened[0]);
      // }
    } catch (error) {
      console.error('Error fetching products with addons:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIngredients = async () => {
    try {
      const response = await axios.get(`${API.BASE_URL}recipe`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const recieps = response?.data?.data?.recieps || [];

      const map = {};
      recieps.forEach((recipe) => {
        const productId = recipe?.product?._id;
        if (productId) {
          map[productId] = recipe.rawMaterials || [];
        }
      });

      setRecipesMap(map);
      console.log('Recipes / ingredients map:', map);
    } catch (error) {
      console.error('Error fetching ingredients/recipes:', error);
    }
  };

  useEffect(() => {
    getCategoriesProductsList();
    getIngredients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box>
        <Grid container className="flex-section">
          {/* LEFT: Products List */}
          <Grid
            sx={{ backgroundColor: '#F6F6F6', paddingTop: '120px' }}
            className="flex-no-scroll"
            item
            md={8}
          >
            <Box
              sx={{
                minHeight: '100vh',
                overflow: 'auto',
                padding: '0 35px 0 35px',
                marginRight: '10px'
              }}
              className="custom-scrollbar"
            >
              <TextField
                fullWidth
                sx={{ borderRadius: '8px', backgroundColor: '#fff' }}
                placeholder="Search"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  handleSearch(value);
                }}
              />

              <Grid container justifyContent={'center'}>
                {/* Category Chips */}
                <Grid item md={12} sx={{ margin: '15px 0 15px 0' }}>
                  <Grid container spacing={1} justifyContent={'center'}>
                    {categories.map((category, index) => (
                      <Grid item xs={2} key={category._id}>
                        <CategoryFilterChip
                          filterText={category.name}
                          isSelected={selectedChipIndex === index}
                          onClick={() => handleChipClick(category._id, index)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                {/* Products List / Loader */}
                {loading ? (
                  <Grid
                    item
                    md={3}
                    sx={{ margin: '20px', textAlign: 'center' }}
                  >
                    <CircularProgress
                      sx={{ color: '#000', margin: '10px 0 15px 0' }}
                    />
                    <br />
                    <Typography variant="paragraph">
                      Fetching Products...
                    </Typography>
                  </Grid>
                ) : (
                  <>
                    {products.length > 0 ? (
                      products.map((product) => (
                        <Grid
                          item
                          md={3}
                          key={product._id}
                          onClick={() => handleProductClick(product)}
                          className={`cursor-p ${selectedProduct?._id === product._id
                            ? 'selected-product'
                            : ''
                            }`}
                          sx={{
                            backgroundColor: product.isExhausted
                              ? '#FECBCB'
                              : '#fff',
                            margin: '20px',
                            textAlign: 'center',
                            padding: '18px 0 18px 0',
                            borderRadius: '12px',
                            position: 'relative'
                          }}
                        >
                          {product.isExhausted && (
                            <ReportProblemIcon
                              sx={{
                                position: 'absolute',
                                color: 'red',
                                paddingLeft: '5px'
                              }}
                            />
                          )}

                          <img
                            src={
                              product.displayImageUrl ||
                              product.displayImage
                            }
                            alt={product.name}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '200px'
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = Images.product;
                            }}
                          />
                          <Typography>{product.name}</Typography>
                        </Grid>
                      ))
                    ) : (
                      <Typography
                        sx={{
                          textAlign: 'center',
                          fontSize: '16px',
                          margin: '20px'
                        }}
                      >
                        No products available.
                      </Typography>
                    )}
                  </>
                )}
              </Grid>
            </Box>
          </Grid>

          {/* RIGHT: Product Details */}
          <Grid item md={4}>
            <Box sx={{ marginTop: '110px', padding: '0 30px 0 30px' }}>
              {selectedProduct ? (
                <Grid item md={12}>
                  <Typography
                    sx={{
                      fontSize: '22px',
                      paddingBottom: '10px',
                      fontWeight: '500'
                    }}
                  >
                    {selectedProduct.name}
                  </Typography>

                  <Grid item md={11}>
                    {selectedProduct.isExhausted && (
                      <Typography
                        sx={{
                          fontSize: '14px',
                          padding: '15px 10px',
                          border: '2px solid red',
                          borderRadius: '6px',
                          backgroundColor: '#FECBCB'
                        }}
                      >
                        Product is exhausted
                      </Typography>
                    )}

                    <Typography
                      sx={{ fontSize: '16px', margin: '15px 0 5px 0' }}
                    >
                      Category
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        borderRadius: '6px',
                        backgroundColor: '#F6F6F6',
                        padding: '5px 0px 5px 10px'
                      }}
                    >
                      {selectedProduct.category?.name || '-'}
                    </Typography>

                    <Typography
                      sx={{ fontSize: '16px', margin: '10px 0 5px 0' }}
                    >
                      Description
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        borderRadius: '6px',
                        backgroundColor: '#F6F6F6',
                        padding: '5px 0px 5px 10px'
                      }}
                    >
                      {selectedProduct.description}
                    </Typography>

                    <Typography
                      sx={{ fontSize: '16px', margin: '10px 0 5px 0' }}
                    >
                      Offer Price
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        borderRadius: '6px',
                        backgroundColor: '#F6F6F6',
                        padding: '5px 0px 5px 10px'
                      }}
                    >
                      ₹{' '}
                      {selectedProduct.newOfferPrice ??
                        selectedProduct.offerPrice ??
                        selectedProduct.defaultOfferPrice ??
                        '-'}
                    </Typography>

                    <Typography
                      sx={{ fontSize: '16px', margin: '10px 0 5px 0' }}
                    >
                      Making Time
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        borderRadius: '6px',
                        backgroundColor: '#F6F6F6',
                        padding: '5px 0px 5px 10px'
                      }}
                    >
                      {selectedProduct.makingTime} minutes
                    </Typography>

                    {selectedProduct.hasAddon &&
                      Array.isArray(selectedProduct.addons) &&
                      selectedProduct.addons.length > 0 && (
                        <>
                          <Typography
                            sx={{
                              fontSize: '16px',
                              margin: '20px 0 8px 0',
                              fontWeight: 500
                            }}
                          >
                            Addons
                          </Typography>

                          <Box
                            sx={{
                              borderRadius: '6px',
                              backgroundColor: '#F6F6F6',
                              padding: '8px 10px'
                            }}
                          >
                            {selectedProduct.addons
                              // Filter addons by outlet like in your other component
                              .filter(
                                (addon) =>
                                  !addon?.excludedOutlets?.includes(outletData?._id)
                              )
                              .map((addon) => {
                                const hasValidValues =
                                  Array.isArray(addon?.values) &&
                                  addon.values.some(
                                    (v) =>
                                      (v?.status !== 'INACTIVE' || v?.isDefault) &&
                                      (!v?.excludedOutlets?.includes(outletData?._id) ||
                                        v?.isDefault)
                                  );

                                if (!hasValidValues) return null;

                                const hasImageValues = addon.values.some(
                                  (v) =>
                                    (v?.selectedImage || v?.nonSelectedImage) &&
                                    (v?.status !== 'INACTIVE' || v?.isDefault) &&
                                    (!v?.excludedOutlets?.includes(outletData?._id) ||
                                      v?.isDefault)
                                );

                                return (
                                  <Grid
                                    container
                                    key={addon._id}
                                    sx={{
                                      padding: '10px 10px 7px 10px',
                                      backgroundColor: '#fff',
                                      marginBottom: '10px',
                                      borderRadius: '18px'
                                    }}
                                  >
                                    {/* Left: badge + title */}
                                    <Grid
                                      item
                                      xs={3}
                                      sx={{
                                        display: 'flex',
                                        paddingTop: '10px',
                                        alignItems: addon?.badgeImage ? 'center' : 'normal'
                                      }}
                                    >
                                      {addon?.badgeImage && (
                                        <img
                                          style={{ width: '40%', paddingRight: '10px' }}
                                          src={addon?.badgeImage}
                                          alt={addon?.title}
                                        />
                                      )}
                                      <Typography className="addonTitle">
                                        {addon?.title}{' '}
                                        {!addon?.isOptional && (
                                          <span
                                            style={{
                                              color: '#D32F2F',
                                              fontSize: '12px'
                                            }}
                                          >
                                            (Required)
                                          </span>
                                        )}
                                      </Typography>
                                    </Grid>

                                    {/* Right: values (image-style or text-style) */}
                                    <Grid item xs={9}>
                                      {hasImageValues ? (
                                        // IMAGE STYLE VALUES (read-only)
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              gap: 2
                                            }}
                                          >
                                            {addon.values
                                              .filter(
                                                (val) =>
                                                  !val?.iconInfo &&
                                                  (val?.status !== 'INACTIVE' || val?.isDefault) &&
                                                  (!val?.excludedOutlets?.includes(
                                                    outletData?._id
                                                  ) ||
                                                    val?.isDefault)
                                              )
                                              .map((addonValue) => {
                                                const isExcluded =
                                                  addonValue?.excludedOutlets?.includes(
                                                    outletData?._id
                                                  );
                                                const isInactiveDefault =
                                                  addonValue?.isDefault &&
                                                  addonValue?.status === 'INACTIVE';
                                                const isDisabled =
                                                  isExcluded || isInactiveDefault;

                                                return (
                                                  <Box
                                                    key={addonValue?._id}
                                                    sx={{
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      textAlign: 'center',
                                                      opacity: isDisabled ? 0.6 : 1,
                                                      filter: isDisabled
                                                        ? 'grayscale(100%) opacity(0.6)'
                                                        : 'none'
                                                    }}
                                                  >
                                                    <img
                                                      src={addonValue?.nonSelectedImage}
                                                      alt={addonValue?.value}
                                                      style={{
                                                        height: 'auto',
                                                        width: 'auto',
                                                        borderRadius: '12px'
                                                      }}
                                                    />
                                                    <Typography
                                                      className="addonValue"
                                                      sx={{ color: '#00A4E2' }}
                                                    >
                                                      {addonValue?.subLabel}
                                                    </Typography>
                                                    <Box>
                                                      <Typography
                                                        color={'green'}
                                                        variant="caption"
                                                      >
                                                        +₹{addonValue?.price ?? 0}
                                                      </Typography>
                                                    </Box>
                                                  </Box>
                                                );
                                              })}
                                          </Box>
                                        </Box>
                                      ) : (
                                        // TEXT STYLE VALUES (read-only)
                                        <Stack spacing={1}>
                                          {addon.values
                                            .filter(
                                              (val) =>
                                                !val?.iconInfo &&
                                                (val?.status !== 'INACTIVE' || val?.isDefault) &&
                                                (!val?.excludedOutlets?.includes(outletData?._id) ||
                                                  val?.isDefault)
                                            )
                                            .map((addonValue) => {
                                              const isExcluded =
                                                addonValue?.excludedOutlets?.includes(
                                                  outletData?._id
                                                );
                                              const isInactiveDefault =
                                                addonValue?.isDefault &&
                                                addonValue?.status === 'INACTIVE';
                                              const isDisabled =
                                                isExcluded || isInactiveDefault;

                                              return (
                                                <Box
                                                  key={addonValue?._id}
                                                  sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-end',
                                                    padding: '4px 0',
                                                    opacity: isDisabled ? 0.6 : 1,
                                                    filter: isDisabled
                                                      ? 'grayscale(100%) opacity(0.6)'
                                                      : 'none'
                                                  }}
                                                >
                                                  <Typography
                                                    className="addonValue"
                                                    sx={{ color: '#00A4E2' }}
                                                  >
                                                    {addonValue?.subLabel}
                                                  </Typography>
                                                  <Box
                                                    className="addonSelect"
                                                    sx={{
                                                      borderRadius: '999px',
                                                      padding: '3px 10px',
                                                      border: '1px solid #00A4E2',
                                                      fontSize: '12px'
                                                    }}
                                                  >
                                                    {addonValue?.value}{' '}
                                                    <span style={{ paddingLeft: '7px' }}>
                                                      +₹{addonValue?.price ?? 0}
                                                    </span>
                                                  </Box>
                                                </Box>
                                              );
                                            })}
                                        </Stack>
                                      )}
                                    </Grid>
                                  </Grid>
                                );
                              })}
                          </Box>
                        </>
                      )}

                    {selectedProduct.hasAddon &&
                      Array.isArray(selectedProduct.addons) &&
                      selectedProduct.addons.length > 0 && (
                        <>
                          <Typography
                            sx={{
                              fontSize: '16px',
                              margin: '20px 0 8px 0',
                              fontWeight: 500
                            }}
                          >
                            Addons
                          </Typography>

                          <Box
                            sx={{
                              borderRadius: '6px',
                              backgroundColor: '#F6F6F6',
                              padding: '8px 10px'
                            }}
                          >
                            {selectedProduct.addons.map((addon) => (
                              <Box
                                key={addon._id}
                                sx={{
                                  marginBottom: '10px',
                                  borderBottom: '1px solid #e0e0e0',
                                  paddingBottom: '8px',
                                  '&:last-of-type': {
                                    borderBottom: 'none',
                                    marginBottom: 0
                                  }
                                }}
                              >
                                {/* Addon title */}
                                <Typography
                                  sx={{
                                    fontSize: '15px',
                                    fontWeight: 500,
                                    marginBottom: '4px'
                                  }}
                                >
                                  {addon.title}{' '}
                                  {!addon.isOptional && (
                                    <span style={{ color: '#D32F2F', fontSize: '12px' }}>
                                      (Required)
                                    </span>
                                  )}
                                </Typography>

                                {/* Addon values */}
                                {Array.isArray(addon.values) && addon.values.length > 0 ? (
                                  addon.values.map((value) => (
                                    <Typography
                                      key={value._id}
                                      sx={{
                                        fontSize: '13px',
                                        marginLeft: '8px',
                                        marginBottom: '2px'
                                      }}
                                    >
                                      • {value.value}
                                      {value.subLabel ? ` (${value.subLabel})` : ''}
                                      {' — '}
                                      ₹{value.price}`
                                      {value.isDefault && ' (Default)'}
                                    </Typography>
                                  ))
                                ) : (
                                  <Typography sx={{ fontSize: '13px', marginLeft: '8px' }}>
                                    No values configured.
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </>
                      )}

                    {/* Ingredients Section */}
                    <Typography
                      sx={{
                        fontSize: '16px',
                        margin: '20px 0 8px 0',
                        fontWeight: 500
                      }}
                    >
                      Ingredients
                    </Typography>

                    {(() => {
                      const ingredients =
                        recipesMap[selectedProduct._id] || [];

                      if (!ingredients.length) {
                        return (
                          <Typography
                            sx={{
                              fontSize: '14px',
                              borderRadius: '6px',
                              backgroundColor: '#F6F6F6',
                              padding: '8px 10px'
                            }}
                          >
                            No ingredients mapped for this product.
                          </Typography>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            borderRadius: '6px',
                            backgroundColor: '#F6F6F6',
                            padding: '8px 10px'
                          }}
                        >
                          {ingredients.map((item) => {
                            const rm = item.rawMaterial || {};
                            const addon = item.addonId;
                            const addonValue = item.addonValueDetails;
                            return (
                              <Box
                                key={item._id}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  padding: '6px 0',
                                  borderBottom: '1px solid #e0e0e0',
                                  '&:last-of-type': {
                                    borderBottom: 'none'
                                  }
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '15px',
                                    fontWeight: 500
                                  }}
                                >
                                  {rm.name || 'Unnamed Raw Material'}
                                </Typography>

                                <Typography sx={{ fontSize: '13px' }}>
                                  Qty:&nbsp;
                                  <b>{item.quantity}</b>
                                  {rm.unit ? ` ${rm.unit}` : ''}
                                </Typography>

                                {addon && (
                                  <Typography sx={{ fontSize: '13px' }}>
                                    Addon:&nbsp;
                                    <b>{addon.title}</b>
                                    {addonValue
                                      ? ` — ${addonValue.value}`
                                      : ''}
                                  </Typography>
                                )}

                                {item.isImportant && (
                                  <Typography
                                    sx={{
                                      fontSize: '12px',
                                      color: '#D32F2F'
                                    }}
                                  >
                                    * Important ingredient
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })()}
                  </Grid>
                </Grid>
              ) : (
                <Grid
                  item
                  md={12}
                  sx={{
                    paddingTop: '25%',
                    textAlign: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography sx={{ fontSize: '22px' }}>
                    Select a product for details
                  </Typography>
                  <img
                    src={Images.noProductSelected}
                    alt="product-not-selcted"
                  />
                </Grid>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default Products;
